//done
// Toast notification system with Animate.css animations.
//
// At most MAX_VISIBLE toasts are on screen, laid out side by side in one row
// (see .toast-container in styles.css). Toasts used to stack without limit, and
// gameplay (raids especially) emits them in bursts, so the column could cover a
// large part of the screen — badly so on tablets. A short row costs far less
// vertical space than a stack. Extra messages queue.
//
// A queue only helps if it keeps up: at the full solo dwell a burst would trail
// gameplay by half a minute. So while messages are waiting the dwell drops to
// BACKLOG_DWELL_MS, and toasts already showing are cut short when something
// queues behind them. The queue is capped, keeping the NEWEST messages — during
// a burst the latest state is what matters, and the raid log keeps the full
// record.

export const MAX_VISIBLE = 2;         // toasts on screen at once (row width in styles.css)
export const SOLO_DWELL_MS = 5000;    // nothing waiting: read at leisure
export const BACKLOG_DWELL_MS = 3000; // something waiting: keep the queue moving
export const EXIT_ANIM_MS = 1000;     // Animate.css default duration
export const MAX_QUEUE = 7;
export const MAX_STICKY = 2;          // see below — why this is capped at all

const queue = [];
const visible = []; // entries on screen: { toast, enterAnimation, exitAnimation, timer, shownAt, dwell }
const sticky = [];  // entries awaiting a click: { toast, exitAnimation }

/**
 * @typedef {{dismiss: () => void, update: (message: string) => void}} StickyHandle
 *   Both are no-ops once the toast is gone, so a caller running a countdown
 *   never has to check whether the player already dismissed it.
 */

/**
 * @param {string} message
 * @param {'info'|'success'|'warning'|'error'} [type]
 * @param {{sticky?: boolean}} [options] - sticky toasts stay until clicked. Use
 *   for things the player must not miss AND cannot recover elsewhere; a message
 *   that merely reports something already visible in the UI does not qualify.
 * @returns {StickyHandle|null} a handle for sticky toasts, else null.
 */
export function addNotification(message, type = 'info', options = {}) {
    // No-op in Node.js environment (for simulation/testing)
    if (typeof document === 'undefined') return null;

    if (options.sticky) return showSticky(message, type);

    queue.push({ message, type });
    if (queue.length > MAX_QUEUE) {
        queue.splice(0, queue.length - MAX_QUEUE); // drop oldest, keep newest
    }

    fillSlots();
    cutShortIfWaiting();
    return null;
}

/**
 * A toast that waits for a click instead of a timer.
 *
 * Deliberately OUTSIDE the MAX_VISIBLE budget and the queue. Sticky toasts have
 * no timer, so letting them hold one of the two transient slots would let two
 * unread messages deadlock the entire notification system - and because the
 * queue keeps only the newest MAX_QUEUE, everything behind them would be
 * discarded silently. A message the player never dismisses must not be able to
 * suppress every message after it.
 *
 * They are still capped. An uncapped pile of undismissable toasts would bury
 * the screen, which is its own way of going unnoticed; past MAX_STICKY the
 * oldest is retired, having had the most time on screen.
 */
function showSticky(message, type) {
    const toastContainer = document.getElementById('toast-container');
    if (!toastContainer) return null;

    const toast = document.createElement('div');
    toast.className = `toast ${type} toast-sticky animate__animated ${getEntranceAnimation(type)}`;
    // Announced rather than just drawn: this is the category of message a player
    // is most likely to be looking away from.
    toast.setAttribute('role', 'alert');

    const content = document.createElement('span');
    content.textContent = message;
    toast.appendChild(content);

    const close = document.createElement('button');
    close.className = 'toast-dismiss';
    close.setAttribute('aria-label', 'Dismiss notification');
    close.textContent = '✕';
    toast.appendChild(close);

    const entry = { toast, exitAnimation: getExitAnimation(type) };
    // The whole toast is the hit target, not just the ✕ - the button is the
    // affordance that says "this one waits for you", not the only way out.
    toast.addEventListener('click', () => dismissSticky(entry));

    toastContainer.appendChild(toast);
    sticky.push(entry);

    while (sticky.length > MAX_STICKY) dismissSticky(sticky[0]);

    // Both no-op once the toast is gone (clicked, or retired past MAX_STICKY),
    // so a caller ticking a countdown into `update` never has to track whether
    // the player got there first.
    return {
        dismiss: () => dismissSticky(entry),
        update: (text) => { if (sticky.includes(entry)) content.textContent = text; },
    };
}

function dismissSticky(entry) {
    const index = sticky.indexOf(entry);
    if (index === -1) return; // already dismissed
    sticky.splice(index, 1);

    entry.toast.classList.add(entry.exitAnimation);
    setTimeout(() => entry.toast.remove(), EXIT_ANIM_MS);
}

// Show queued messages until the row is full.
function fillSlots() {
    while (visible.length < MAX_VISIBLE && queue.length > 0) {
        if (!showNext()) return; // no container — nothing more we can do
    }
}

// Something is queued behind the visible toasts — shorten their remaining time
// so the backlog drains promptly instead of waiting out full solo dwells.
function cutShortIfWaiting() {
    if (queue.length === 0) return;

    for (const entry of visible) {
        if (entry.dwell === BACKLOG_DWELL_MS) continue; // already hurried
        const remaining = Math.max(0, BACKLOG_DWELL_MS - (Date.now() - entry.shownAt));
        clearTimeout(entry.timer);
        entry.dwell = BACKLOG_DWELL_MS;
        entry.timer = setTimeout(() => dismiss(entry), remaining);
    }
}

// Renders the next queued message. Returns false if there's nowhere to put it.
function showNext() {
    const next = queue.shift();
    if (!next) return false;

    const toastContainer = document.getElementById('toast-container');
    if (!toastContainer) {
        queue.length = 0;
        return false;
    }

    // Choose entrance animation based on notification type
    const enterAnimation = getEntranceAnimation(next.type);
    const exitAnimation = getExitAnimation(next.type);

    // Create toast element
    const toast = document.createElement('div');
    toast.className = `toast ${next.type} animate__animated ${enterAnimation}`;
    toast.textContent = next.message;

    // Add to container
    toastContainer.appendChild(toast);

    const dwell = queue.length > 0 ? BACKLOG_DWELL_MS : SOLO_DWELL_MS;
    const entry = { toast, enterAnimation, exitAnimation, shownAt: Date.now(), dwell, timer: null };
    entry.timer = setTimeout(() => dismiss(entry), dwell);
    visible.push(entry);
    return true;
}

// Play the exit animation, remove the node, then backfill the freed slot. The
// entry keeps its slot until the animation finishes — it's still on screen.
function dismiss(entry) {
    if (!visible.includes(entry)) return; // already dismissed

    entry.toast.classList.remove(entry.enterAnimation);
    entry.toast.classList.add(entry.exitAnimation);

    setTimeout(() => {
        if (entry.toast.parentNode) {
            entry.toast.parentNode.removeChild(entry.toast);
        }
        const index = visible.indexOf(entry);
        if (index !== -1) visible.splice(index, 1);
        fillSlots();
    }, EXIT_ANIM_MS);
}

// Get entrance animation based on notification type
export function getEntranceAnimation(type) {
    switch (type) {
        case 'success':
            return 'animate__bounceInRight';
        case 'error':
            return 'animate__shake';
        case 'warning':
            return 'animate__slideInDown';
        case 'info':
        default:
            return 'animate__slideInRight';
    }
}

// Get exit animation based on notification type
export function getExitAnimation(type) {
    switch (type) {
        case 'success':
            return 'animate__bounceOutRight';
        case 'error':
            return 'animate__slideOutRight';
        case 'warning':
            return 'animate__slideOutUp';
        case 'info':
        default:
            return 'animate__slideOutRight';
    }
}
